import { formatCop } from '@paguz/ui';
import { formatRate } from '../../lib/format.ts';

/** Cambio de una hoja del objeto de configuración (`autoApproval.maxLimit`). */
export interface Change {
  path: string;
  before: unknown;
  after: unknown;
}

function isMoney(value: unknown): value is { amount: string; currency: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'amount' in value &&
    'currency' in value &&
    Object.keys(value).length === 2
  );
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && !isMoney(value);
}

/** Diferencias hoja a hoja (dinero y arreglos se comparan como un valor). */
export function diffValues(before: unknown, after: unknown, prefix = ''): Change[] {
  if (isPlainObject(before) && isPlainObject(after)) {
    const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
    return keys.flatMap((key) =>
      diffValues(before[key], after[key], prefix ? `${prefix}.${key}` : key),
    );
  }
  return JSON.stringify(before) === JSON.stringify(after) ? [] : [{ path: prefix, before, after }];
}

/** Presentación legible de un valor de configuración. */
export function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (isMoney(value)) return formatCop(value.amount);
  if (typeof value === 'string' && /^\d{1,3}\.\d{6}$/.test(value)) return formatRate(value, '');
  if (Array.isArray(value)) {
    if (value.length === 0) return 'ninguno';
    return value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ');
  }
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

/** Actualización inmutable por ruta (`['autoApproval', 'enabled']`). */
export function setIn<T>(target: T, path: readonly (string | number)[], value: unknown): T {
  const [head, ...rest] = path;
  if (head === undefined) return value as T;
  if (Array.isArray(target)) {
    const copy: unknown[] = [...(target as unknown[])];
    copy[head as number] = setIn(copy[head as number], rest, value);
    return copy as T;
  }
  const obj = (target ?? {}) as Record<string, unknown>;
  return { ...obj, [head]: setIn(obj[head], rest, value) } as T;
}
